import { ResponsiveImage } from '@natu/ui/ResponsiveImage';
import { cn } from '@natu/utils/cn';

import {
  type SBProps,
  type Spacing,
  type StoryblokAsset,
  DynamicRender,
  resolveStoryblokStyles,
  sbEditable,
  getAssetFromStoryblok,
} from '../../../../utils';
import type { SBContainerProps } from '../SBContainer/SBContainer';

interface SBContainerWithImageProps extends SBContainerProps {
  image?: StoryblokAsset;
  imageDesktop?: StoryblokAsset;
  topNotch?: Spacing;
  topNotchTablet?: Spacing;
  topNotchDesktop?: Spacing;
}

export const SBContainerWithImage = ({ blok }: SBProps<SBContainerWithImageProps>) => {
  const {
    image,
    imageDesktop,
    topNotch,
    topNotchTablet,
    topNotchDesktop,
    bgColor,
    body,
    containerSize,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    pxMobile,
    pxTablet,
    pxDesktop,
    pyMobile,
    pyTablet,
    pyDesktop,
    rtlMobile,
    rtlTablet,
    rtlDesktop,
    rtrMobile,
    rtrTablet,
    rtrDesktop,
    rblMobile,
    rblTablet,
    rblDesktop,
    rbrMobile,
    rbrTablet,
    rbrDesktop,
  } = blok;

  const classNames = {
    wrapper: resolveStoryblokStyles({
      size: containerSize,
      mt: mtMobile,
      mtTablet,
      mtDesktop,
      mb: mbMobile,
      mbTablet,
      mbDesktop,
    }),
    content: resolveStoryblokStyles({
      pr: pxMobile,
      prTablet: pxTablet,
      prDesktop: pxDesktop,
      pl: pxMobile,
      plTablet: pxTablet,
      plDesktop: pxDesktop,
      pt: pyMobile,
      ptTablet: pyTablet,
      ptDesktop: pyDesktop,
      pb: pyMobile,
      pbTablet: pyTablet,
      pbDesktop: pyDesktop,
      rtlMobile,
      rtlTablet,
      rtlDesktop,
      rtrMobile,
      rtrTablet,
      rtrDesktop,
      rblMobile,
      rblTablet,
      rblDesktop,
      rbrMobile,
      rbrTablet,
      rbrDesktop,
      className: bgColor?.value ? 'bg-[var(--bg-sb-color)]' : undefined,
    }),
    image: resolveStoryblokStyles({
      topNotch,
      topNotchTablet,
      topNotchDesktop,
    }),
  };

  const style = bgColor?.value
    ? ({ '--bg-sb-color': bgColor?.value } as React.CSSProperties)
    : undefined;

  const imageData = image && getAssetFromStoryblok(image, { type: 'image' });
  const imageDesktopData = imageDesktop && getAssetFromStoryblok(imageDesktop, { type: 'image' });

  if (!imageData?.src) {
    return null;
  }

  return (
    <section className={classNames.wrapper} style={style} {...sbEditable(blok)}>
      <div className={cn('relative z-[1]', classNames.content)}>
        <DynamicRender data={body} />
      </div>
      <div className={classNames.image}>
        <ResponsiveImage
          className={cn('w-full', imageDesktopData?.src && 'md:hidden')}
          {...imageData}
        />
        {imageDesktopData?.src && (
          <ResponsiveImage className="hidden w-full md:block" {...imageDesktopData} />
        )}
      </div>
    </section>
  );
};
